import { motion, AnimatePresence } from "framer-motion";
import { Download, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { downloadComparison } from "../lib/specSheet";
import { useLang } from "../i18n";

// Floating tray shown while grades are selected for comparison.
export const CompareTray = ({ selected, onOpen, onClear, onRemove }) => {
  const { t } = useLang();
  const C = t.compare;
  return (
    <AnimatePresence>
      {selected.length > 0 && (
        <motion.div
          initial={{ y: 110 }} animate={{ y: 0 }} exit={{ y: 110 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="compare-tray"
          className="fixed bottom-0 inset-x-0 z-40 bg-clara-espresso/95 backdrop-blur-md border-t border-clara-cream/15 text-clara-cream"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-4">
            <span className="font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted hidden sm:block">{C.upTo3}</span>
            <div className="flex items-center gap-2">
              {selected.map((code) => (
                <span key={code} className="inline-flex items-center gap-2 rounded-full bg-clara-cream/10 border border-clara-cream/20 px-4 py-1.5 font-data text-xs">
                  {code}
                  <button onClick={() => onRemove(code)} data-testid={`compare-remove-${code}`} aria-label={`Remove ${code}`} className="text-clara-cream/60 hover:text-clara-cream transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-3">
              <span className="font-data text-xs text-clara-cream/60">{selected.length}/3 {C.selected}</span>
              <button onClick={onClear} data-testid="compare-clear-btn" className="font-data text-xs uppercase tracking-wider text-clara-cream/60 hover:text-clara-cream transition-colors">
                {C.clear}
              </button>
              <button
                onClick={onOpen}
                disabled={selected.length < 2}
                data-testid="compare-open-btn"
                className="rounded-full bg-clara-terracotta px-6 py-2.5 text-sm font-semibold text-clara-cream hover:bg-clara-cream hover:text-clara-espresso transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                {C.open}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Side-by-side spec table with differences highlighted.
export const CompareModal = ({ selected, products, open, onClose }) => {
  const { t } = useLang();
  const C = t.compare;
  const P = t.products;
  const L = P.specLabels;
  const tv = (v) => P.values[v] || v;
  const sel = selected.map((c) => products.find((p) => p.code === c)).filter(Boolean);

  const ROWS = [
    [P.typeLabel, (p) => P.types[p.type]],
    [L.Color, (p) => P.colours[p.color]],
    [L.pH, (p) => p.ph],
    [L.Flavor, (p) => tv(p.physical.flavor)],
    [L["Fat Content"], (p) => p.physical.fat_content],
    [L.Fineness, (p) => p.physical.fineness],
    [L.Moisture, (p) => p.physical.moisture],
    [L["Shell Content"], (p) => p.physical.shell_content],
    [L["Total Plate Count"], (p) => p.microbiological.total_plate_count],
    [L.Yeast, (p) => p.microbiological.yeast],
    [L.Mold, (p) => p.microbiological.mold],
    [L.Enterobacteriaceae, (p) => tv(p.microbiological.enterobacteriaceae)],
    [L.Salmonella, (p) => tv(p.microbiological.salmonella)],
    [L["E. Coli"], (p) => tv(p.microbiological.e_coli)],
    [L.Coliform, (p) => tv(p.microbiological.coliform)],
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl p-0 border-0 bg-clara-espresso text-clara-cream overflow-hidden max-h-[92vh] overflow-y-auto noise-overlay" data-testid="compare-modal">
        <DialogTitle className="sr-only">{C.title}</DialogTitle>
        <button onClick={onClose} data-testid="compare-close-btn" aria-label="Close" className="absolute top-4 right-4 z-20 text-clara-cream/70 hover:text-clara-cream transition-colors">
          <X className="w-5 h-5" />
        </button>
        <div className="relative z-10 p-8 lg:p-10">
          <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight mb-2">{C.title}</h3>
          <p className="text-sm text-clara-darkmuted mb-8">{C.diffNote}</p>
          <div className="overflow-x-auto">
            <table className="w-full font-data text-xs" data-testid="compare-table">
              <thead>
                <tr className="border-b border-clara-cream/20">
                  <th className="text-left py-3 pr-4 w-40" />
                  {sel.map((p) => (
                    <th key={p.code} className="text-left py-3 pr-4 align-bottom min-w-[140px]">
                      <div className="relative w-full aspect-[16/9] mb-3 overflow-hidden border border-clara-cream/15" style={{ backgroundColor: p.hex }}>
                        <img src={p.image} alt={`Clara ${p.code} cocoa powder`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                      <div className="font-display text-2xl font-normal">{p.code}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, fn]) => {
                  const vals = sel.map(fn);
                  const diff = new Set(vals).size > 1;
                  return (
                    <tr key={label} className="border-b border-clara-cream/10" data-testid={`compare-row-${label.replace(/[\s.]+/g, "-").toLowerCase()}`}>
                      <td className="py-2.5 pr-4 text-clara-darkmuted">{label}</td>
                      {vals.map((v, i) => (
                        <td key={sel[i].code} className={`py-2.5 pr-4 ${diff ? "text-clara-terracotta font-semibold" : "text-clara-cream"}`}>
                          {v}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={() => downloadComparison(sel, ROWS)} data-testid="compare-download-pdf" className="mt-8 inline-flex items-center gap-2 rounded-full border border-clara-cream/30 px-6 py-3 text-sm font-semibold text-clara-cream hover:border-clara-cream transition-colors">
            <Download className="w-4 h-4" /> {C.downloadPdf}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
